import React, { useState } from 'react';
import {
	Button,
	ButtonGroup,
	Icon,
	TextPassage,
	Table,
	Grid,
	Section,
	Searchbar,
	Toolbar,
	ToolbarItem,
} from '../common';
import SubscriptionRequest from '../../models/entities/SubscriptionRequest';
import ExemptionDetailsFormWrapper from '../common/wrappers/ExemptionDetailsFormWrapper';
import TextInputField from '../common/form-controls/TextInputField';
import { useExemptionDetailsForm, ExemptionDetails } from './ExemptionDetailsForm';
import { ExemptionCategoryInput } from './components/ExemptionCategoryInput';
import { ResourceGroupEntry } from './components/AddResourceGroupModal';
import RiskBadge from './components/RiskBadge';
import { PolicyDefinitionResponse } from '../../models/viewModels/PolicyExemptionRequests/PolicyDefinitionResponse';
import { PolicyAssignmentResponse } from '../../models/viewModels/PolicyExemptionRequests/PolicyAssignmentResponse';

export interface PolicyExemptionPolicyProps {
	selectedSubscriptions: SubscriptionRequest[];
	entriesBySubscription: Record<number, ResourceGroupEntry[]>;
	selectedAssignmentBySubscription: Record<number, PolicyAssignmentResponse>;
	initialSelectedPoliciesBySubscription?: Record<number, PolicyDefinitionResponse[]>;
	initialExemptionDetails?: ExemptionDetails;
	onNext: (
		selectedPoliciesBySubscription: Record<number, PolicyDefinitionResponse[]>,
		exemptionDetails: ExemptionDetails
	) => void;
	onBack: () => void;
}

const PolicyExemptionRequestSelectPolicy = ({
	selectedSubscriptions,
	entriesBySubscription,
	selectedAssignmentBySubscription,
	initialSelectedPoliciesBySubscription,
	initialExemptionDetails,
	onNext,
	onBack,
}: PolicyExemptionPolicyProps) => {
	const { form, stateFormHandler } = useExemptionDetailsForm(initialExemptionDetails);
	const [selectedPoliciesBySubscription, setSelectedPoliciesBySubscription] = useState<
		Record<number, PolicyDefinitionResponse[]>
	>(() => initialSelectedPoliciesBySubscription ?? {});
	const [searchBySubscription, setSearchBySubscription] = useState<Record<number, string>>({});

	const handleBack = () => {
		onBack();
	};

	const handleNext = () => {
		onNext(selectedPoliciesBySubscription, form);
	};

	const handlePolicySelect = (subscriptionId: number, policy: PolicyDefinitionResponse) => {
		console.log(policy);
		setSelectedPoliciesBySubscription((prev) => {
			const currentAssignment = prev[subscriptionId] ?? [];
			const policyAlreadySelected = currentAssignment.some((p) => p.id === policy.id);
			return {
				...prev,
				[subscriptionId]: policyAlreadySelected
					? currentAssignment.filter((p) => p.id !== policy.id)
					: [...currentAssignment, policy],
			};
		});
	};

	const hasSelectedPolicies = selectedSubscriptions.every(
		(sub) => (selectedPoliciesBySubscription[sub.id] ?? []).length > 0
	);

	const isFormValid = !!form.category && !!form.exceptionDescription && !!form.justification;

	const canProceed = selectedSubscriptions.length > 0 && hasSelectedPolicies && isFormValid;

	return (
		<>
			<TextPassage>
				<p>
					Select the policy definitions for each subscription group. For initiatives, always select specific
					definitions to avoid broad exemptions.
				</p>
			</TextPassage>
			<ExemptionDetailsFormWrapper title={'Exemption Details'}>
				<>
					<TextInputField label="Risk Assessment Link" {...stateFormHandler.link} />
					<Grid variant="halves">
						<Grid.Item>
							<ExemptionCategoryInput {...stateFormHandler.category} />
						</Grid.Item>
						<Grid.Item>
							<TextInputField label="Exception Description" {...stateFormHandler.exceptionDescription} />
						</Grid.Item>
					</Grid>
					<TextInputField label="Business Justification" {...stateFormHandler.justification} />
				</>
			</ExemptionDetailsFormWrapper>

			{selectedSubscriptions.length === 0 ? (
				<TextPassage>
					<p>No subscriptions selected. Please go back and select at least one subscription.</p>
				</TextPassage>
			) : (
				<>
					{selectedSubscriptions.map((sub, index) => (
						<Section
							key={sub.id}
							title={
								sub.createdSubscriptionName ?? sub.createdSubscriptionId ?? `Subscription ${index + 1}`
							}
							type="em-c-section--expandable"
							isInError={(selectedPoliciesBySubscription[sub.id] ?? []).length === 0}
							titleFontSize="large"
							marginNone
						>
							<Table
								header={
									<Toolbar>
										<ToolbarItem width="lg">
											<Searchbar
												condensed
												value={searchBySubscription[sub.id] ?? ''}
												onChange={(e) =>
													setSearchBySubscription((prev) => ({
														...prev,
														[sub.id]: e.target.value,
													}))
												}
												onClick={() =>
													setSearchBySubscription((prev) => ({ ...prev, [sub.id]: '' }))
												}
												placeholder="Search policies…"
											/>
										</ToolbarItem>
									</Toolbar>
								}
							>
								<Table.Head>
									<Table.Head.Row>
										<Table.Head.Cell> </Table.Head.Cell>
										<Table.Head.Cell> Policy </Table.Head.Cell>
										<Table.Head.Cell> Description </Table.Head.Cell>
										<Table.Head.Cell> Category </Table.Head.Cell>
										<Table.Head.Cell> Risk </Table.Head.Cell>
									</Table.Head.Row>
								</Table.Head>
								<Table.Body>
									{(selectedAssignmentBySubscription[sub.id]?.properties?.policyDefinitions ?? [])
										.filter((policy) => {
											const q = (searchBySubscription[sub.id] ?? '').toLowerCase().trim();
											if (!q) return true;
											return (
												policy.properties?.displayName?.toLowerCase().includes(q) ||
												policy.properties?.description?.toLowerCase().includes(q) ||
												policy.properties?.metadata?.category?.toLowerCase().includes(q)
											);
										})
										.map((policy) => {
											const isSelected = (selectedPoliciesBySubscription[sub.id] ?? []).some(
												(p) => p.id === policy.id
											);
											return (
												<Table.Row
													key={policy.id}
													onClick={() => handlePolicySelect(sub.id, policy)}
												>
													<Table.Cell>
														<input
															type="checkbox"
															checked={isSelected}
															onChange={() => handlePolicySelect(sub.id, policy)}
														/>
													</Table.Cell>
													<Table.Cell> {policy.properties?.displayName} </Table.Cell>
													<Table.Cell> {policy.properties?.description} </Table.Cell>
													<Table.Cell> {policy.properties?.metadata?.category} </Table.Cell>
													<Table.Cell>
														<RiskBadge risk={policy.properties?.metadata?.xomRiskRating} />
													</Table.Cell>
												</Table.Row>
											);
										})}
								</Table.Body>
							</Table>
						</Section>
					))}
				</>
			)}

			<br />
			<ButtonGroup>
				<Button variant="secondary" size="small" onClick={handleBack}>
					<Icon icon="chevron-left" size="small" />
					<span>Back</span>
				</Button>
				<Button variant="primary" size="small" disabled={!canProceed} onClick={handleNext}>
					<span>Next Step</span>
					<Icon icon="chevron-right" size="small" />
				</Button>
			</ButtonGroup>
		</>
	);
};

export default PolicyExemptionRequestSelectPolicy;
